import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default icon paths (Vite bundling issue)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const goldIcon = new L.DivIcon({
  className: "",
  html: `<div style="width:28px;height:28px;border-radius:50%;background:hsl(var(--primary));border:3px solid #000;box-shadow:0 2px 8px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;color:#000;font-weight:700;font-size:12px;">📍</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

type Point = { name: string; lat: number; lng: number };

function isValid(p: Point | null): boolean {
  return !!p && Number.isFinite(p.lat) && Number.isFinite(p.lng) && !(p.lat === 0 && p.lng === 0);
}

async function geocode(name: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const q = encodeURIComponent(`${name}, Dominican Republic`);
    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${q}`, {
      headers: { Accept: "application/json" },
    });
    const arr = await res.json();
    if (Array.isArray(arr) && arr[0]) return { lat: Number(arr[0].lat), lng: Number(arr[0].lon) };
  } catch {}
  return null;
}

function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length < 1) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
    } else {
      map.fitBounds(points as any, { padding: [40, 40] });
    }
  }, [JSON.stringify(points)]);
  return null;
}

export default function RouteMap({ from, to }: { from: Point | null; to: Point | null }) {
  const [fromResolved, setFromResolved] = useState<Point | null>(null);
  const [toResolved, setToResolved] = useState<Point | null>(null);

  useEffect(() => {
    let cancel = false;
    const resolve = async (p: Point | null, set: (v: Point | null) => void) => {
      if (!p) { set(null); return; }
      if (isValid(p)) { set(p); return; }
      const g = await geocode(p.name);
      if (!cancel) set(g ? { name: p.name, lat: g.lat, lng: g.lng } : null);
    };
    resolve(from, setFromResolved);
    resolve(to, setToResolved);
    return () => { cancel = true; };
  }, [from?.name, from?.lat, from?.lng, to?.name, to?.lat, to?.lng]);

  const points = useMemo(() => {
    const pts: [number, number][] = [];
    if (fromResolved) pts.push([fromResolved.lat, fromResolved.lng]);
    if (toResolved) pts.push([toResolved.lat, toResolved.lng]);
    return pts;
  }, [fromResolved, toResolved]);

  const center: [number, number] = points[0] ?? [18.7357, -70.1627]; // DR center fallback

  return (
    <div className="rounded-xl overflow-hidden border border-primary/20 shadow-lg" style={{ height: 320 }}>
      <MapContainer center={center} zoom={8} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {fromResolved && (
          <Marker position={[fromResolved.lat, fromResolved.lng]} icon={goldIcon}>
            <Popup>Pickup: {fromResolved.name}</Popup>
          </Marker>
        )}
        {toResolved && (
          <Marker position={[toResolved.lat, toResolved.lng]} icon={goldIcon}>
            <Popup>Drop-off: {toResolved.name}</Popup>
          </Marker>
        )}
        {points.length === 2 && (
          <Polyline positions={points} pathOptions={{ color: "#d4a017", weight: 4, dashArray: "8 8" }} />
        )}
        <FitBounds points={points} />
      </MapContainer>
    </div>
  );
}
